import type { ReactNode } from "react";
import { Radio } from "lucide-react";

import type { FactImpact } from "../api";

/**
 * How far this approval reaches, read before the button in DOM order.
 *
 * KET-3853 closed having corrected 1 published panel of 13, because nothing ever
 * showed how many surfaces a value reached (C3 interaction rule 3). The count
 * comes from `GET /v1/facts/{id}/impact` and from nowhere else.
 *
 * Four distinct claims, never collapsed into each other:
 *   - a number we were given         → "Changes 4 published panels across UK and EU"
 *   - a zero we were given           → "This is not published anywhere yet"
 *   - a key with no fact to ask about → "Nothing is published for this yet"
 *   - a request that did not succeed → "We could not confirm…"
 *
 * The last NEVER renders as zero. A fabricated count is a lie; a silent zero is
 * the same lie in the most reassuring possible form. And a first value is not a
 * failed lookup: there is genuinely nothing to count yet, which is a statement
 * about the fact, not about our ability to read it.
 */
export function BlastRadiusLine({
  impact,
  pending,
  failed,
  firstValue = false,
  variant = "card",
}: {
  impact: FactImpact | undefined;
  pending: boolean;
  failed: boolean;
  /** No fact exists yet, so there is nothing whose reach could be counted. */
  firstValue?: boolean;
  /**
   * `card` — the tinted box, in the pane, where it is the last thing read before
   * Approve. `note` — a plain icon row, in the confirm dialog, where it is the
   * first of three answers (reach, source, who) set the same way. Boxing only
   * the first made it read as a warning the other two were not
   * (outcome-audit cycle 3, M9).
   */
  variant?: "card" | "note";
}) {
  const Reach = variant === "note" ? ReachNote : ReachCard;
  if (firstValue) {
    return (
      <Reach tone="quiet">
        Nothing is published for this yet, so there is no reach to count — approving it will fill
        the blank wherever this fact is used.
      </Reach>
    );
  }

  if (pending) {
    return <Reach tone="quiet">Checking how far this reaches…</Reach>;
  }

  if (failed || impact === undefined) {
    return (
      <Reach tone="quiet">
        We could not confirm how many surfaces this reaches.{" "}
        <span className="text-ks-grey-1">The correction can still be published.</span>
      </Reach>
    );
  }

  if (impact.surfaces === 0) {
    return (
      <Reach tone="quiet">
        This is not published anywhere yet — approving it will not change a live surface today.
      </Reach>
    );
  }

  return (
    <Reach tone="brand">
      {/* The COUNT carries the weight, the markets are its qualifier — as the
          mock-up sets it. The whole sentence in bold left nothing to find. */}
      <span className="font-bold">
        Changes {impact.surfaces} published {impact.surfaces === 1 ? "panel" : "panels"}
      </span>
      {impact.markets.length > 0 ? ` across ${listMarkets(impact.markets)}` : ""}.
    </Reach>
  );
}

/**
 * The card the reach is stated inside — a tinted box beside the broadcast mark,
 * as the approved mock-up draws it.
 *
 * It is a CARD rather than a loose sentence because of where it sits: the last
 * thing read before the button that publishes. A line of body text there scans
 * as more prose in a pane already full of it, and the count is the one sentence
 * that must not be skimmed (KET-3853 closed having corrected 1 panel of 13).
 *
 * Two tones, and the split is a claim about confidence, not decoration. `brand`
 * — the purple tint — is only for a count we actually HAVE. Every other answer
 * (still looking, could not confirm, published nowhere, no fact yet) is a
 * statement that there is no reach to report, and dressing that in the same
 * confident tint would make "we could not look" read like a finding.
 */
function ReachCard({ tone, children }: { tone: "brand" | "quiet"; children: ReactNode }) {
  return (
    <div
      className={`flex gap-2.5 rounded-ks-lg border px-3.5 py-3 ${
        tone === "brand" ? "border-ks-purple-3 bg-ks-purple-4" : "border-ks-grey-4"
      }`}
    >
      <Radio
        aria-hidden
        className={`mt-px size-[18px] shrink-0 ${
          tone === "brand" ? "text-ks-purple" : "text-ks-grey-1"
        }`}
      />
      <p className="text-sm leading-snug text-ks-ink">{children}</p>
    </div>
  );
}

/** "UK, EU and GCC" — the way a person reads a list, not "UK, EU, GCC". */
export function listMarkets(markets: string[]): string {
  const shown = markets.map((market) => market.toUpperCase());
  if (shown.length <= 1) return shown.join("");
  return `${shown.slice(0, -1).join(", ")} and ${shown[shown.length - 1]}`;
}

/**
 * The same claim as one marked line of the confirm dialog, matching its
 * "Checked against" and "Self-approved" rows. The tone split survives as the
 * mark: purple only for a count we have, grey for every other answer.
 */
function ReachNote({ tone, children }: { tone: "brand" | "quiet"; children: ReactNode }) {
  return (
    <div className="flex items-start gap-2.5">
      <Radio
        aria-hidden
        className={`mt-0.5 size-[18px] shrink-0 ${
          tone === "brand" ? "text-ks-purple" : "text-ks-grey-1"
        }`}
      />
      <p className="text-sm leading-[1.5] text-ks-ink">{children}</p>
    </div>
  );
}
